/**
 * Resolves the canonical Questionnaire reference to store on QuestionnaireResponse.
 *
 * **Important:** Pilot AccessPolicy gives providers `Questionnaire` as **read-only** — they must
 * never POST new Questionnaire definitions (that returns 403 Forbidden). When the UI uses a
 * client-side id that differs from the server (e.g. mock fallback), we resolve by `url` or
 * business `name` (slug) so the saved QR points at the seeded Questionnaire.
 */
import type { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire } from '@medplum/fhirtypes';

const NOT_FOUND_MESSAGE =
  'This note template was not found in Medplum. Providers cannot create templates—run the pilot seed (or publish Questionnaires) and pick a template from the library, or reload when online.';

export async function ensureQuestionnaireOnServer(
  medplum: ReturnType<typeof useMedplum>,
  questionnaire: Questionnaire,
): Promise<string> {
  if (questionnaire.id) {
    try {
      const existing = await medplum.readResource('Questionnaire', questionnaire.id);
      return existing.url?.trim() || `Questionnaire/${existing.id}`;
    } catch {
      // Id may be client-only (e.g. mock id) — try url / name below.
    }
  }

  const url = questionnaire.url?.trim();
  if (url) {
    try {
      const byUrl = await medplum.searchResources('Questionnaire', {
        url,
        _count: '10',
      });
      const match = byUrl.find((q) => q.url === url) ?? byUrl[0];
      if (match?.id) {
        return match.url?.trim() || `Questionnaire/${match.id}`;
      }
    } catch {
      // continue
    }
  }

  const name = questionnaire.name?.trim();
  if (name) {
    try {
      const byName = await medplum.searchResources('Questionnaire', {
        name,
        status: 'active',
        _count: '20',
      });
      const match = byName.find((q) => q.name === name) ?? byName[0];
      if (match?.id) {
        return match.url?.trim() || `Questionnaire/${match.id}`;
      }
    } catch {
      // continue
    }
  }

  throw new Error(NOT_FOUND_MESSAGE);
}
